import { type AttackerData, attackerSize, isFrozen } from '../entities/Attacker';
import { DEFAULT_NUM_QUEUES } from '../config/constants';

export class QueueSystem {
  /**
   * Per-lane arrays. Each lane is an ordered list of attackers from front (index 0) to back.
   * A multi-orb attacker has one entry in each of its lanes, and each advances independently.
   */
  private lanes: (AttackerData | null)[][];
  private _cachedLength: number = 0;
  private readonly _numQueues: number;


  constructor(initialQueue: AttackerData[], numQueues: number = DEFAULT_NUM_QUEUES) {
    this._numQueues = numQueues;
    this.lanes = Array.from({ length: this._numQueues }, () => []);
    // Place attackers into lanes. Each attacker is assigned lane(s) and appended to the back.
    // We use the same packing logic: try to fit into current "virtual row" of free lane tails,
    // respecting adjacency for multi-orb attackers.

    // Track the current tail depth per lane (how many entries are in each lane)
    // We pack attackers so that a multi-orb attacker's orbs all start at the same depth
    // (they'll independently advance later after the first collapse).
    const laneDepths = Array(this._numQueues).fill(0);

    for (const attacker of initialQueue) {
      const size = attackerSize(attacker);
      // Find adjacent lanes where all have the same (minimum) depth
      const startLane = this.findBestLanes(laneDepths, size);
      const lanes = Array.from({ length: size }, (_, i) => startLane + i);
      attacker.queueLanes = lanes;

      // Pad shorter lanes so the attacker starts at the same depth in all its lanes
      const maxDepth = Math.max(...lanes.map(l => laneDepths[l]));
      for (const lane of lanes) {
        while (this.lanes[lane].length < maxDepth) {
          this.lanes[lane].push(null); // padding
        }
        this.lanes[lane].push(attacker);
        laneDepths[lane] = this.lanes[lane].length;
      }
    }

    // Collapse immediately to advance orbs as far forward as possible
    this.collapseLanes();
    this._cachedLength = initialQueue.length;
  }

  /**
   * Find the best starting lane for a multi-orb attacker of given size.
   * Picks adjacent lanes that minimize the max depth (so the attacker is placed as far forward as possible).
   */
  private findBestLanes(laneDepths: number[], size: number): number {
    let bestStart = 0;
    let bestMaxDepth = Infinity;
    for (let start = 0; start <= this._numQueues - size; start++) {
      let maxD = 0;
      for (let i = 0; i < size; i++) {
        maxD = Math.max(maxD, laneDepths[start + i]);
      }
      if (maxD < bestMaxDepth) {
        bestMaxDepth = maxD;
        bestStart = start;
      }
    }
    return bestStart;
  }

  /** Total unique attackers remaining across all lanes. */
  queueLength(): number {
    return this._cachedLength;
  }

  /**
   * Peek at the front attacker in a specific lane.
   * Returns the attacker only if it's at position 0 in this lane AND ready to dispatch
   * (i.e., at position 0 in ALL of its lanes).
   */
  peekQueue(laneIndex: number): AttackerData | null {
    const lane = this.lanes[laneIndex];
    if (lane.length === 0 || lane[0] === null) return null;
    const attacker = lane[0];
    // Hammers are not dispatchable by player tap — they auto-trigger via checkForHammer()
    if (attacker.isHammer) return null;
    if (isFrozen(attacker)) return null;
    if (!this.isReadyToDispatch(attacker)) return null;
    return attacker;
  }

  /** Check if a multi-lane attacker is at position 0 in all its lanes. */
  private isReadyToDispatch(attacker: AttackerData): boolean {
    const lanes = attacker.queueLanes ?? [0];
    for (const l of lanes) {
      if (this.lanes[l].length === 0 || this.lanes[l][0] !== attacker) return false;
    }
    return true;
  }

  /** Dequeue front attacker from a specific queue lane (removes from position 0, collapses). */
  dequeueFrom(laneIndex: number): AttackerData | null {
    const lane = this.lanes[laneIndex];
    if (lane.length === 0 || lane[0] === null) return null;
    const attacker = lane[0];
    // Hammers are not dispatchable by player tap — they auto-trigger via checkForHammer()
    if (attacker.isHammer) return null;
    if (isFrozen(attacker)) return null;
    if (!this.isReadyToDispatch(attacker)) return null;

    // Remove from position 0 in all its lanes
    const attackerLanes = attacker.queueLanes ?? [laneIndex];
    for (const l of attackerLanes) {
      const idx = this.lanes[l].indexOf(attacker);
      if (idx !== -1) {
        this.lanes[l].splice(idx, 1);
      }
    }
    attacker.state = 'on_conveyor';
    this._cachedLength--;

    // Collapse: advance orbs forward independently
    this.collapseLanes();
    return attacker;
  }

  /**
   * Collapse lanes: remove null gaps so each orb advances as far forward as possible.
   * Each orb moves independently in its own lane.
   */
  private collapseLanes(): void {
    for (let l = 0; l < this._numQueues; l++) {
      // Remove all nulls — each entry slides forward to fill gaps
      this.lanes[l] = this.lanes[l].filter(entry => entry !== null);
    }
  }



  /** Peek at front of first non-empty lane that's ready to dispatch. */
  peekFront(): AttackerData | null {
    for (let lane = 0; lane < this._numQueues; lane++) {
      const a = this.peekQueue(lane);
      if (a) return a;
    }
    return null;
  }

  /**
   * Get a specific queue lane as an ordered array of attackers (front to back).
   */
  getQueueLane(laneIndex: number): readonly AttackerData[] {
    return this.lanes[laneIndex].filter(entry => entry !== null) as AttackerData[];
  }

  /** Get all queue lanes. */
  getAllQueues(): readonly (readonly AttackerData[])[] {
    const result: AttackerData[][] = [];
    for (let lane = 0; lane < this._numQueues; lane++) {
      result.push(this.getQueueLane(lane) as AttackerData[]);
    }
    return result;
  }

  /** Number of queue lanes. */
  numLanes(): number {
    return this._numQueues;
  }

  /**
   * Get the row grid (reconstructed from per-lane data for rendering compatibility).
   * Row 0 is the front. Each row has numQueues slots.
   */
  getRows(): readonly (readonly (AttackerData | null)[])[] {
    const maxLen = Math.max(0, ...this.lanes.map(l => l.length));
    const rows: (AttackerData | null)[][] = [];
    for (let i = 0; i < maxLen; i++) {
      const row: (AttackerData | null)[] = [];
      for (let lane = 0; lane < this._numQueues; lane++) {
        row.push(i < this.lanes[lane].length ? this.lanes[lane][i] : null);
      }
      rows.push(row);
    }
    return rows;
  }

  /** Get the depth (position index) of an attacker in a specific lane. Returns -1 if not found. */
  getDepth(attackerId: number, laneIndex: number): number {
    const lane = this.lanes[laneIndex];
    for (let i = 0; i < lane.length; i++) {
      if (lane[i]?.id === attackerId) return i;
    }
    return -1;
  }




  /**
   * Check if the front item of any lane is a hammer. If so, dequeue it and return it.
   * Hammers auto-trigger when they reach the front — they're not tappable by the player.
   * Returns null if no hammer is at the front of any lane.
   */
  checkForHammer(): AttackerData | null {
    for (let lane = 0; lane < this._numQueues; lane++) {
      const front = this.lanes[lane][0];
      if (!front) continue;
      if (!front.isHammer) continue;
      // Hammer found at front of this lane — dequeue it
      const hammerLanes = front.queueLanes ?? [lane];
      for (const l of hammerLanes) {
        const idx = this.lanes[l].indexOf(front);
        if (idx !== -1) {
          this.lanes[l].splice(idx, 1);
        }
      }
      this._cachedLength--;
      this.collapseLanes();
      return front;
    }
    return null;
  }

  /** Check if the front attacker in a given lane is frozen. */
  isFrontFrozen(laneIndex: number): boolean {
    const lane = this.lanes[laneIndex];
    if (lane.length === 0 || lane[0] === null) return false;
    return isFrozen(lane[0]);
  }

  /**
   * Decrement all frozen counters by 1. Called when an attacker is exhausted on the conveyor.
   * Returns the list of attackers that just thawed (counter hit 0).
   */
  thawFrozen(): AttackerData[] {
    const thawed: AttackerData[] = [];
    const seen = new Set<number>();

    for (const lane of this.lanes) {
      for (const entry of lane) {
        if (!entry || seen.has(entry.id)) continue;
        seen.add(entry.id);

        if (isFrozen(entry)) {
          const f = entry.frozen ?? 0;
          entry.frozen = f - 1;
          if (entry.frozen <= 0) {
            entry.frozen = undefined;
            thawed.push(entry);
          }
        }
      }
    }
    return thawed;
  }

  /**
   * Returns true if there is any dispatchable item at the front of any queue lane.
   * This includes non-frozen attackers ready to dispatch, or hammers that will auto-trigger.
   */
  canDispatchAny(): boolean {
    for (let lane = 0; lane < this._numQueues; lane++) {
      const front = this.lanes[lane][0];
      if (!front) continue;
      // Hammer at front → progress is possible (auto-triggers)
      if (front.isHammer) return true;
      // Non-frozen attacker that is ready to dispatch → progress is possible
      if (!isFrozen(front) && this.isReadyToDispatch(front)) return true;
    }
    return false;
  }

  /**
   * Returns true if there is any non-frozen attacker anywhere in the queue (not just at fronts).
   * Used by the deadlock detector to avoid premature "Frozen Out!" when non-frozen items
   * exist behind frozen lane fronts — those will eventually become dispatchable as frozen
   * items ahead of them thaw.
   */
  hasAnyNonFrozenItems(): boolean {
    const seen = new Set<number>();
    for (const lane of this.lanes) {
      for (const entry of lane) {
        if (!entry || seen.has(entry.id)) continue;
        seen.add(entry.id);
        if (!isFrozen(entry) && !entry.isHammer) return true;
      }
    }
    return false;
  }

  /**
   * Peek at the frozen attacker at the front of the given lane, if any.
   * Unlike {@link peekQueue}, this does NOT filter out frozen attackers — it
   * returns the front attacker regardless of its frozen counter, as long as it
   * is at position 0 in ALL of its lanes (consensus) and is not a hammer.
   *
   * Used by the stuck-on-frozen-front force-dispatch path.
   */
  peekFrozenFront(laneIndex: number): AttackerData | null {
    const lane = this.lanes[laneIndex];
    if (lane.length === 0 || lane[0] === null) return null;
    const attacker = lane[0];
    if (attacker.isHammer) return null;
    if (!this.isReadyToDispatch(attacker)) return null;
    return attacker;
  }

  /**
   * Force-dispatch the attacker at the front of `laneIndex`, bypassing the
   * normal frozen-attacker block. The attacker's `frozen` counter is cleared
   * (it's being dispatched, so its frozen state is no longer meaningful).
   *
   * Returns null if no attacker is at position 0 (with lane consensus) or if
   * the front is a hammer.
   *
   * This is the runtime equivalent of `StrategicSimulator`'s force-dispatch
   * fallback and `AtomicSimulator.applyDispatch`'s stuck-state escape hatch.
   */
  forceDispatchFrom(laneIndex: number): AttackerData | null {
    const lane = this.lanes[laneIndex];
    if (lane.length === 0 || lane[0] === null) return null;
    const attacker = lane[0];
    if (attacker.isHammer) return null;
    if (!this.isReadyToDispatch(attacker)) return null;

    const attackerLanes = attacker.queueLanes ?? [laneIndex];
    for (const l of attackerLanes) {
      const idx = this.lanes[l].indexOf(attacker);
      if (idx !== -1) {
        this.lanes[l].splice(idx, 1);
      }
    }
    attacker.state = 'on_conveyor';
    // Clear frozen counter — the attacker is now on the conveyor, frozen state no longer applies.
    attacker.frozen = undefined;
    this._cachedLength--;

    this.collapseLanes();
    return attacker;
  }
}
